import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  structuredData?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  structuredData,
}: SEOHeadProps) {
  const location = useLocation();
  const finalCanonical = canonicalUrl || `https://www.91anime.com${location.pathname}`;
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, propName?: string) => {
      let el: HTMLMetaElement | null;
      if (propName) {
        el = document.querySelector(`meta[property="${propName}"]`);
      } else {
        el = document.querySelector(`meta[name="${name}"]`);
      }
      if (el) {
        el.setAttribute('content', content);
      } else {
        const meta = document.createElement('meta');
        if (propName) {
          meta.setAttribute('property', propName);
        } else {
          meta.setAttribute('name', name);
        }
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (el) {
        el.setAttribute('href', href);
      } else {
        const link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('href', href);
        document.head.appendChild(link);
      }
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setLink('canonical', finalCanonical);

    setMeta('', finalOgTitle, 'og:title');
    setMeta('', finalOgDescription, 'og:description');
    setMeta('', ogType, 'og:type');
    setMeta('', finalCanonical, 'og:url');
    if (ogImage) {
      setMeta('', ogImage, 'og:image');
    }

    setMeta('', 'summary_large_image', 'twitter:card');
    setMeta('', finalOgTitle, 'twitter:title');
    setMeta('', finalOgDescription, 'twitter:description');
    if (ogImage) {
      setMeta('', ogImage, 'twitter:image');
    }

    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }
    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    return () => {
      const sdScript = document.querySelector('script[type="application/ld+json"]');
      if (sdScript && sdScript.textContent === JSON.stringify(structuredData)) {
        sdScript.remove();
      }
    };
  }, [title, description, keywords, finalCanonical, finalOgTitle, finalOgDescription, ogImage, ogType, structuredData]);

  return null;
}